import { marketDate, validateDateRange } from "./upcoming-earnings-data-source.js";

const PAGE_SIZE = 100;
const MAX_PAGES = 200;
const REPORT_TIMES = new Set(["Unspecified", "BeforeMarketOpen", "AfterMarketClose", "DuringMarket"]);
const GUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i;

// Recent reads canonical reported events, never elapsed calendar forecasts.
export class RecentEarningsDataSource {
  constructor({ endpoint = "/api/v1/recent-earnings", fetchImpl = globalThis.fetch, now = () => new Date() } = {}) {
    this.endpoint = endpoint;
    this.fetchImpl = fetchImpl.bind(globalThis);
    this.now = now;
  }

  async load({ from, to, signal, onProgress = () => {} }) {
    validateDateRange(from, to);
    if (to > marketDate(this.now())) throw new Error("Recent earnings must use reported dates through today.");
    const items = [];
    const seen = new Set();
    let offset = 0;
    let metadata = null;
    let fingerprint = null;
    for (let pageNumber = 0; pageNumber < MAX_PAGES; pageNumber += 1) {
      signal?.throwIfAborted();
      const url = new URL(this.endpoint, globalThis.location?.href || "http://localhost/");
      url.searchParams.set("from", from); url.searchParams.set("to", to);
      url.searchParams.set("offset", String(offset)); url.searchParams.set("limit", String(PAGE_SIZE));
      const response = await this.fetchImpl(url.toString(), {
        signal, credentials: "same-origin", headers: { Accept: "application/json" }
      });
      signal?.throwIfAborted();
      if (!response.ok) throw new Error(response.status === 401 || response.status === 403
        ? "Sign in to view recent earnings." : "Recent earnings could not be loaded. Please try again.");
      let page;
      try { page = await response.json(); } catch { throw incomplete(); }
      signal?.throwIfAborted();
      if (!page || page.source !== "CanonicalReported" || page.from !== from || page.to !== to
        || page.offset !== offset || page.limit !== PAGE_SIZE || !Array.isArray(page.items) || page.items.length > PAGE_SIZE
        || !Number.isSafeInteger(page.totalCount) || page.totalCount < 0
        || typeof page.coverageNote !== "string" || !page.coverageNote.trim()
        || typeof page.dateSemantics !== "string" || !page.dateSemantics.trim()) throw incomplete();
      const current = JSON.stringify([page.source, page.totalCount, page.coverageNote, page.dateSemantics]);
      if (metadata && current !== fingerprint) throw new Error("The reported earnings changed while loading. Please try again.");
      if (!metadata) {
        const { items: ignored, ...summary } = page;
        metadata = summary; fingerprint = current;
      }
      for (const item of page.items) {
        if (!validItem(item, from, to) || seen.has(item.id)) throw incomplete();
        seen.add(item.id); items.push(item);
      }
      if (items.length > page.totalCount) throw incomplete();
      onProgress({ loaded: items.length, total: page.totalCount });
      if (page.nextOffset === null) {
        if (items.length !== page.totalCount) throw incomplete();
        return { items, metadata };
      }
      if (!Number.isSafeInteger(page.nextOffset) || page.nextOffset !== offset + page.items.length
        || page.items.length === 0 || page.nextOffset >= page.totalCount) throw incomplete();
      offset = page.nextOffset;
    }
    throw new Error("There are too many reported earnings to load. Please try again later.");
  }
}

function validItem(item, from, to) {
  if (!item || !validId(item.id) || !validId(item.symbolId)
    || typeof item.symbol !== "string" || !item.symbol.trim()
    || typeof item.companyName !== "string" || !item.companyName.trim()
    || typeof item.actualEps !== "number" || !Number.isFinite(item.actualEps)
    || !(item.estimatedEps === null || typeof item.estimatedEps === "number" && Number.isFinite(item.estimatedEps))
    || typeof item.source !== "string" || !item.source.trim() || !publicUrl(item.sourceUrl)
    || !["AutomaticallyVerified", "ManuallyVerified"].includes(item.verification)
    || typeof item.verificationNote !== "string" || !item.verificationNote.trim()
    || !REPORT_TIMES.has(item.reportTime)) return false;
  try { validateDateRange(item.reportedDate, item.reportedDate); } catch { return false; }
  return item.reportedDate >= from && item.reportedDate <= to;
}
function validId(value) { return typeof value === "string" && GUID.test(value) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(value); }
function publicUrl(value) {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password && url.hostname.includes(".")
      && !["localhost", "[::1]"].includes(url.hostname) && !url.hostname.endsWith(".local")
      && !url.hostname.endsWith(".internal") && !url.hostname.endsWith(".localhost")
      && !url.hostname.includes(":") && !/^\d+(?:\.\d+){3}$/.test(url.hostname);
  } catch { return false; }
}
function incomplete() { return new Error("The reported earnings response was incomplete. Please try again."); }
